import type { ReactElement } from "react";

import { useDispatch } from "react-redux";

import type { BaseActivatedPanelProps } from "app/pages/sales-intelligence/common-components/table-actions-panel";
import { useListParams } from "app/pages/sales-intelligence/modules/lists/context/ListParamsContext";
import {
    useInvalidateOpportunityListContactsResults,
    useOpportunityListContactsResults,
} from "app/pages/sales-intelligence/pages/opportunity-list/hooks/useOpportunityListContactsResults";
import {
    exportSelectedContactsToExcelThunk,
    fetchTopNContactsThunk,
} from "app/pages/sales-intelligence/pages/opportunity-list/store/effects";
import CRMContactsExportPanelContainer from "app/pages/sales-intelligence/sub-modules/contacts-search/containers/CRMContactsExportPanel";
import useActivePanelLifeCycle from "app/pages/sales-intelligence/sub-modules/contacts-search/hooks/useActivePanelLifeCycle";
import type { ContactSearchEntry } from "app/pages/sales-intelligence/sub-modules/contacts-search/types/common";
import type { ExportToExcelFn } from "app/pages/sales-intelligence/sub-modules/contacts-search/types/exportFlow";
import { useCurrentOpportunityListCountry } from "app/pages/sales-intelligence/sub-modules/opportunities/hooks/useCurrentOpportunityListCountry";

const ContactsExportPanelContainer = (props: BaseActivatedPanelProps): ReactElement => {
    const dispatch = useDispatch();

    const listParams = useListParams();
    const country = useCurrentOpportunityListCountry();

    const { data, isFetching } = useOpportunityListContactsResults();
    const invalidateContacts = useInvalidateOpportunityListContactsResults();

    useActivePanelLifeCycle();

    async function fetchTopNContacts(n: number): Promise<ContactSearchEntry[]> {
        return dispatch(fetchTopNContactsThunk(n, listParams));
    }

    const handleExcelExportSubmit: ExportToExcelFn = async (topNOrPairs, isSilent) =>
        dispatch(exportSelectedContactsToExcelThunk(listParams, topNOrPairs, isSilent));

    return (
        <CRMContactsExportPanelContainer
            contacts={data?.rows || []}
            totalCount={data?.totalCount || 0}
            isLoading={isFetching}
            countries={[country]}
            onDeactivate={props.onDeactivate}
            onSuccess={invalidateContacts}
            onExportToExcel={handleExcelExportSubmit}
            onFetchTopNContacts={fetchTopNContacts}
        />
    );
};

export default ContactsExportPanelContainer;
